import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LifeBuoy, RefreshCw, Loader2, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import DoppelteCheckins from '@/components/DoppelteCheckins';

/* Support & Umstieg (29.09.2026): alles, woran Mitglieder beim Wechsel von Octiv
   zur P1 App hängen bleiben, an einer Stelle — plus der Fortschritt des Umstiegs.
   Die eigentliche Arbeit läuft über die bestehenden Wege (Profil, Freigabe,
   Zusammenführen); hier wird gesammelt, verlinkt und auf App-Anfragen geantwortet. */
const P1_ROT = 'hsl(var(--marke))';   // im CRM die gewählte Akzentfarbe (Neugestaltung 12.10.2026)
const datum = (iso) => iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}. ${String(iso).slice(11, 16)}` : '';
const FARBE = { app_zugang: P1_ROT, ticket: '#2563EB', datenabgleich: '#D97706', familie: '#7C3AED', dublette: '#DC2626', ohne_zugang: '#6B7280' };

function Kachel({ wert, label, sub }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-2xl font-bold tabular-nums">{wert}</div>
      <div className="text-xs font-medium">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Dublette({ item, onFertig }) {
  const d = item.dublette;
  const [ziel, setZiel] = useState(d.ziel_vorschlag);
  const [probe, setProbe] = useState(null);
  const [busy, setBusy] = useState(false);
  const quelle = d.personen.filter(p => p.id !== ziel);
  const probelauf = async () => {
    setBusy(true);
    try {
      const r = [];
      for (const q of quelle) r.push((await api.post(`/leads/${ziel}/zusammenfuehren/${q.id}?probe=true`)).data);
      setProbe(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Probelauf fehlgeschlagen'); }
    setBusy(false);
  };
  const ausfuehren = async () => {
    if (!window.confirm(`${quelle.length} Eintrag/Einträge in den gewählten Eintrag zusammenführen? Die anderen werden danach gelöscht.`)) return;
    setBusy(true);
    try {
      for (const q of quelle) await api.post(`/leads/${ziel}/zusammenfuehren/${q.id}?probe=false`);
      toast.success('Zusammengeführt – das Mitglied muss sich in der App einmal neu anmelden'); onFertig();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Zusammenführen fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <div className="mt-2 space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-muted-foreground text-left"><th className="py-1 pr-2">Behalten</th><th className="pr-2">Name</th><th className="pr-2">Herkunft</th><th className="pr-2">Angelegt</th><th className="pr-2">Einheiten</th><th className="pr-2">App</th><th>Mitgliedschaft</th></tr></thead>
          <tbody>
            {d.personen.map(p => (
              <tr key={p.id} className="border-t border-border/50">
                <td className="py-1 pr-2"><input type="radio" name={`ziel-${d.email}`} checked={ziel === p.id} onChange={() => { setZiel(p.id); setProbe(null); }} className="accent-marke" /></td>
                <td className="pr-2"><a href={`/leads/${p.id}`} className="underline">{p.name}</a></td>
                <td className="pr-2">{p.source || '–'}</td>
                <td className="pr-2">{String(p.created_at || '').slice(0, 10)}</td>
                <td className="pr-2 tabular-nums">{p.einheiten}</td>
                <td className="pr-2">{p.app_sitzungen ? 'ja' : '–'}</td>
                <td>{p.mitgliedschaft ? 'aktiv' : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {probe && <p className="text-xs text-muted-foreground">Zieht um: {probe.map(p => Object.entries(p.umzug || {}).map(([k, v]) => `${k.split('.')[0]} ${v}`).join(', ') || 'nichts').join(' | ')}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" disabled={busy} onClick={probelauf}>Probelauf</Button>
        <Button size="sm" disabled={busy || !probe} onClick={ausfuehren} title={!probe ? 'Erst Probelauf' : ''}>Zusammenführen</Button>
      </div>
    </div>
  );
}

function Eintrag({ it, onFertig }) {
  const navigate = useNavigate();
  const [antwort, setAntwort] = useState('');
  const [busy, setBusy] = useState(false);
  const person = it.person;
  const los = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onFertig(); } catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(false);
  };
  const ticket = (status) => los(() => api.post(`/support/tickets/${it.id}/antwort`, { text: antwort, status }), status === 'geloest' ? 'Gelöst' : 'Antwort gesendet');

  return (
    <Card className="border-border/60" data-testid={`support-${it.key}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-white rounded px-1.5 py-0.5" style={{ background: FARBE[it.quelle] }}>{it.herkunft === 'Hilfe-Formular' ? 'Hilfe-Formular' : it.quelle_label}</span>
          <p className="font-semibold text-sm flex-1 min-w-[12rem]">{it.titel}</p>
          <span className="text-xs text-muted-foreground">{datum(it.erstellt_am)}</span>
        </div>
        {it.text && <p className="text-sm text-muted-foreground whitespace-pre-line">{it.text}</p>}
        {(it.verlauf || []).map((v, i) => <p key={i} className="text-xs border-l-2 pl-2" style={{ borderColor: P1_ROT }}><b>{v.von}:</b> {v.text}</p>)}
        <div className="flex items-center gap-2 flex-wrap">
          {person?.id && <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate(`/leads/${person.id}`)}><ExternalLink className="w-3.5 h-3.5" /> {person.name || 'Profil'}</Button>}
          {it.quelle === 'app_zugang' && <>
            {person?.id && <Button size="sm" disabled={busy} onClick={() => los(() => api.patch(`/public/admin/onboarding-requests/${it.id}`, { lead_id: person.id, status: 'freigegeben' }), 'Freigegeben – Handynummer übernommen, Anmeldung klappt jetzt')}>Freigeben ({person.name})</Button>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => los(() => api.patch(`/public/admin/onboarding-requests/${it.id}`, { status: 'abgelehnt' }), 'Abgelehnt')}>Ablehnen</Button>
          </>}
          {it.quelle === 'familie' && <>
            <Button size="sm" disabled={busy} onClick={() => los(() => api.patch(`/public/family/admin/link-requests/${it.id}`, { status: 'erledigt' }), 'Erledigt – Push an die Eltern')} title="Erst im Profil unter Familie verknüpfen">Verknüpft – erledigt</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => los(() => api.patch(`/public/family/admin/link-requests/${it.id}`, { status: 'abgelehnt' }), 'Abgelehnt')}>Ablehnen</Button>
          </>}
          {it.quelle === 'datenabgleich' && <span className="text-xs text-muted-foreground">Im Profil unter „Datenabgleich aus der App“ je Feld übernehmen.</span>}
          {it.quelle === 'ohne_zugang' && <span className="text-xs text-muted-foreground">Im Profil E-Mail eintragen oder Kind mit Elternkonto verknüpfen.</span>}
        </div>
        {it.quelle === 'ticket' && (
          <div className="space-y-2">
            <Textarea value={antwort} onChange={e => setAntwort(e.target.value)} placeholder={it.herkunft === 'Hilfe-Formular' ? (it.kann_mail ? 'Antwort (geht per E-Mail an die angegebene Adresse)' : 'Keine E-Mail angegeben – bitte anrufen, hier nur Notiz') : 'Antwort ans Mitglied (geht per Push und E-Mail)'} rows={2} className="text-sm" />
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" disabled={busy || !antwort.trim()} onClick={() => ticket('in_bearbeitung')}>Antworten</Button>
              <Button size="sm" variant="outline" disabled={busy || !antwort.trim()} onClick={() => ticket('geloest')}>Antworten &amp; gelöst</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => ticket('geloest')}>Ohne Antwort als gelöst markieren</Button>
            </div>
          </div>
        )}
        {it.quelle === 'dublette' && <Dublette item={it} onFertig={onFertig} />}
      </CardContent>
    </Card>
  );
}

export default function SupportPage() {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filter, setFilter] = useState('alle');
  const [listeOffen, setListeOffen] = useState(false);
  const lade = useCallback(async () => {
    setLaden(true);
    try { setDaten((await api.get('/support/uebersicht')).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht geladen werden'); }
    setLaden(false);
  }, []);
  useEffect(() => { lade(); }, [lade]);
  const liste = useMemo(() => (daten?.eingang || []).filter(i => filter === 'alle' || i.quelle === filter), [daten, filter]);

  const f = daten?.fortschritt;
  const quote = f && f.aktive ? Math.round((f.in_app / f.aktive) * 100) : 0;
  return (
    <div className="space-y-5 fade-in" data-testid="support-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><LifeBuoy className="w-6 h-6" style={{ color: P1_ROT }} /> Support &amp; Umstieg</h1>
          <p className="text-muted-foreground text-sm">Alles, woran Mitglieder beim Wechsel von Octiv zur P1 App hängen bleiben – an einer Stelle.</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={lade} disabled={laden}>{laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aktualisieren</Button>
      </div>

      {f && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kachel wert={`${f.in_app} / ${f.aktive}`} label={`In der App (${quote} %)`} sub={`${f.selbst} selbst, ${f.ueber_eltern} über Elternkonto`} />
            <Kachel wert={f.aktive - f.in_app} label="Noch nicht umgestiegen" sub="aktive Mitglieder ohne App-Anmeldung" />
            <Kachel wert={f.daten_bestaetigt} label="Daten bestätigt" sub="Datenabgleich nach dem ersten Login" />
            <Kachel wert={f.ohne_zugang} label="Kein Zugang möglich" sub="ohne E-Mail und ohne Elternkonto" />
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden" aria-label={`Umstieg ${quote} Prozent`}><div className="h-full" style={{ width: `${quote}%`, background: P1_ROT }} /></div>
          <button type="button" className="text-xs underline flex items-center gap-1" onClick={() => setListeOffen(o => !o)}>
            {listeOffen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Wer ist noch nicht in der App? ({f.noch_nicht_in_app.length})
          </button>
          {listeOffen && (
            <div className="flex flex-wrap gap-1.5">
              {f.noch_nicht_in_app.map(p => (
                <button key={p.id} type="button" onClick={() => navigate(`/leads/${p.id}`)} className={`rounded-full border px-2.5 py-1 text-xs hover:bg-muted ${p.hat_email || p.elternkonto ? 'border-border' : 'border-red-400 text-red-600'}`} title={p.hat_email ? '' : 'keine E-Mail'}>{p.name}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Datenpflege: nur für die Leitung sichtbar (die Karte prüft das selbst) */}
      <DoppelteCheckins />

      <div className="flex gap-1.5 flex-wrap">
        <button type="button" onClick={() => setFilter('alle')} className={`rounded-full px-3 py-1 text-xs border ${filter === 'alle' ? 'text-white border-transparent' : 'border-border'}`} style={filter === 'alle' ? { background: P1_ROT } : undefined}>Alle ({daten?.eingang?.length || 0})</button>
        {Object.entries(daten?.quellen || {}).map(([k, l]) => (daten?.zaehler?.[k] ? (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs border ${filter === k ? 'text-white border-transparent' : 'border-border'}`} style={filter === k ? { background: FARBE[k] } : undefined}>{l} ({daten.zaehler[k]})</button>
        ) : null))}
      </div>

      {laden && !daten ? <p className="text-sm text-muted-foreground">Lade …</p> : liste.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nichts offen. 🎉</p>
      ) : (
        <div className="space-y-3">{liste.map(it => <Eintrag key={it.key} it={it} onFertig={lade} />)}</div>
      )}
    </div>
  );
}
